import { dayKeyInZone } from '@/lib/dayKey';
import type { PublicMatch } from './scheduleStatus';
import { OTHER_COURT_ID } from './slotGrid';

const MINUTE = 60_000;
export interface TimelinePlacement {
  match: PublicMatch;
  courtIndex: number;
  lane: number;
  laneCount: number;
  startMs: number;
  endMs: number;
  topRem: number;
  heightRem: number;
}
export interface ScheduleTimeline {
  startMs: number;
  endMs: number;
  remPerMinute: number;
  heightRem: number;
  laneCounts: number[];
  placements: TimelinePlacement[];
  ticks: { atMs: number; topRem: number; major: boolean }[];
}
function interval(match: PublicMatch) {
  const startMs = Date.parse(match.scheduledAt);
  if (!Number.isFinite(startMs)) return null;
  // Estimates outside (0, 24h] are malformed; use the same 60min fallback everywhere.
  const duration =
    Number.isFinite(match.durationEstimateMin) &&
    match.durationEstimateMin > 0 &&
    match.durationEstimateMin <= 24 * 60
      ? match.durationEstimateMin
      : 60;
  const endMs = startMs + duration * MINUTE;
  if (!Number.isFinite(new Date(endMs).getTime())) return null;
  return { match, startMs, endMs, duration };
}

/** Malformed cached/configured display zones must not break the calendar. */
export function normalizeTimelineZone(zone: string): string {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone });
    return zone;
  } catch {
    return 'UTC';
  }
}

/** Full-day axis stays stable; connected overlaps divide their fixed court width. */
export function buildScheduleTimeline(
  matches: PublicMatch[],
  courtIds: string[],
  displayZone: string,
  axisMatches = matches,
): ScheduleTimeline | null {
  const all = axisMatches.map(interval).filter((m) => m !== null);
  if (!all.length) return null;
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: normalizeTimelineZone(displayZone),
    minute: '2-digit',
  });
  const minute = (ms: number) => Number(formatter.format(new Date(ms)));
  let startMs = Math.floor(Math.min(...all.map((m) => m.startMs)) / MINUTE) * MINUTE;
  let endMs = Math.ceil(Math.max(...all.map((m) => m.endMs)) / MINUTE) * MINUTE;
  // A selected-day payload plus at most a 24h estimate fits this bound, including DST.
  if (endMs - startMs > 72 * 60 * MINUTE) return null;
  while (minute(startMs) % 30 !== 0) startMs -= MINUTE;
  while (minute(endMs) % 30 !== 0) endMs += MINUTE;
  const remPerMinute = Math.max(0.5, 3 / Math.min(...all.map((m) => m.duration)));
  const top = (ms: number) => ((ms - startMs) / MINUTE) * remPerMinute;
  const colByCourt = new Map(courtIds.map((id, index) => [id, index]));
  const groups = courtIds.map(() => ({
    ends: [] as number[],
    endMs: -Infinity,
    placements: [] as TimelinePlacement[],
  }));
  const laneCounts = courtIds.map(() => 1);
  const finishGroup = (courtIndex: number) => {
    const group = groups[courtIndex];
    const count = Math.max(1, group.ends.length);
    for (const placement of group.placements) placement.laneCount = count;
    laneCounts[courtIndex] = Math.max(laneCounts[courtIndex], count);
    group.ends = [];
    group.placements = [];
    group.endMs = -Infinity;
  };
  const placements: TimelinePlacement[] = [];
  const visible = matches
    .map(interval)
    .filter((m) => m !== null)
    .sort(
      (a, b) => a.startMs - b.startMs || a.endMs - b.endMs || a.match.id.localeCompare(b.match.id),
    );
  for (const m of visible) {
    const courtIndex = colByCourt.get(m.match.courtId) ?? colByCourt.get(OTHER_COURT_ID);
    if (courtIndex === undefined) continue;
    const group = groups[courtIndex];
    if (m.startMs >= group.endMs) finishGroup(courtIndex);
    const ends = group.ends;
    let lane = ends.findIndex((end) => end <= m.startMs);
    if (lane === -1) lane = ends.length;
    ends[lane] = m.endMs;
    const placement: TimelinePlacement = {
      match: m.match,
      courtIndex,
      lane,
      laneCount: 1,
      startMs: m.startMs,
      endMs: m.endMs,
      topRem: top(m.startMs),
      heightRem: m.duration * remPerMinute,
    };
    placements.push(placement);
    group.placements.push(placement);
    group.endMs = Math.max(group.endMs, m.endMs);
  }
  groups.forEach((_, index) => finishGroup(index));
  const ticks: ScheduleTimeline['ticks'] = [];
  // Walk instants, not wall-clock arithmetic: repeated/missing DST hours remain honest.
  for (let atMs = startMs; atMs <= endMs; atMs += MINUTE) {
    const localMinute = minute(atMs);
    if (localMinute % 15 === 0)
      ticks.push({ atMs, topRem: top(atMs), major: localMinute % 30 === 0 });
  }
  return {
    startMs,
    endMs,
    remPerMinute,
    heightRem: top(endMs),
    laneCounts,
    placements,
    ticks,
  };
}

export function timelineNow(
  timeline: ScheduleTimeline,
  nowMs: number,
  scope: { day: string; zone: string },
) {
  if (
    !Number.isFinite(nowMs) ||
    dayKeyInZone(new Date(nowMs).toISOString(), scope.zone) !== scope.day
  )
    return null;
  if (nowMs < timeline.startMs) return { topRem: 0, edge: 'before' as const };
  if (nowMs > timeline.endMs) return { topRem: timeline.heightRem, edge: 'after' as const };
  return { topRem: ((nowMs - timeline.startMs) / MINUTE) * timeline.remPerMinute, edge: null };
}
